import React, { Suspense, useEffect, useState, lazy, type ReactNode, type ComponentType } from 'react'
import { NotFound, Redirect, Route, Switch, useHistory } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { GroupsProvider, useGroupsContext } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { useUser } from '@fs/zion-user'
import { getActiveRole } from '@fs/zion-admin-mode'
import { useFeatureFlag } from '@fs/zion-flags'
import { AppLayout, colors } from '@fs/zion-ui'
import { SelectSiblingPedigreeButton, SelectSiblingPedigreeOverlay } from '@fs/zion-tree-pedigree'
import usePreloadPreferences from './services/usePreloadPreferences'
import TreeChangesPage, { TreeChangesPageSkeleton } from './pages/TreeChangesPage'
import GroupDetailsPage from './pages/GroupDetailsPage'
import ConnectCreatePage from './pages/ConnectCreatePage'
import FamilyGroupsListPage from './pages/FamilyGroupsListPage'
import UnavailableDecorator from './components/UnavailableDecorator'
import { SubnavWrapper } from './components/SubnavWrapper'
import DataAdminProvider from './providers/DataAdminProvider'
import AdminRoute from './components/AdminRoute'
import AppLoading from './components/AppLoading'
import CenteredLoadingSpinner from './components/CenteredLoadingSpinner'
import TreeIdToGroupId from './pages/TreeIdToGroupId'
import ManageTreesPage, { ManageTreesPageSkeleton } from './pages/ManageTreesPage'
import UsabillaAction from './UsabillaAction'
import TreeSettingsPage, { SettingsPageSkeleton } from './pages/TreeSettingsPage'
import {
  CONNECT_CREATE_DONE_PATH,
  CONNECT_CREATE_PATH,
  FAMILY_GROUP_LIST_PATH,
  FAMILY_GROUP_PATH,
  GENEALOGIES_FILES_PATH,
  OLD_RESEARCHER_PAGE_PATH,
  TREE_SETTINGS_PATH,
  SWITCH_TREE_PATH,
  TREE_CHANGES_PATH,
  TREES_PATH,
  RESEARCH_TREE_LIST_PATH,
  TREE_SETTINGS_NO_TAB_PATH,
} from './routes'
import { GroupListSkeleton } from './components/group-lists/GroupList'
import GroupDetailsPageSkeletons from './pages/GroupDetailsPageSkeletons'

// ---------------- Tree Import (migrated) lazy imports ----------------
// These were originally defined inside src/tree-import/App.js. We lazy load to keep initial bundle smaller.
const ComparePage = lazy(() => import('./tree-import/components/compare-page/ComparePage'))
const LandingPage = lazy(() => import('./tree-import/components/landing-page/LandingPage'))
const MatchesPage = lazy(() => import('./tree-import/components/matches-page/MatchesPage'))
const ChooseOldOrNewImportPage = lazy(() => import('./tree-import/components/import-page/ChooseOldOrNewImportPage'))
const RealTimeStats = lazy(() => import('./tree-import/components/admin/real-time-stats/RealTimeStats'))
const RecentTrees = lazy(() => import('./tree-import/components/admin/recent-trees/RecentTrees'))
const TreeStats = lazy(() => import('./tree-import/components/admin/tree-stats/TreeStats'))
const TreePersons = lazy(() => import('./tree-import/components/admin/tree-persons/TreePersons'))
const UserMatchesPage = lazy(() => import('./tree-import/components/matches-page/UserMatchesPage'))

type GroupsLoadingGateProps = {
  children: ReactNode
  loader: ReactNode
}

const GroupsLoadingGate = ({ children, loader }: GroupsLoadingGateProps): JSX.Element => {
  const { loading } = useGroupsContext()
  if (loading) {
    return <>{loader}</>
  }
  return <>{children}</>
}

type EnhancedTreeRouteProps = {
  path: string | string[]
  backgroundColor?: string
  exact?: boolean
  LoadingComponent: ComponentType
  component?: ComponentType
  children?: ReactNode
  requirePersonalData?: boolean
} & ({ children: ReactNode } | { component: ComponentType })

/**
 * Wrapper around Route that applies the AppLayout and Suspense for lazy loaded components.
 */
const EnhancedTreeRoute = ({
  path,
  backgroundColor,
  exact,
  LoadingComponent,
  ...props
}: EnhancedTreeRouteProps): JSX.Element => (
  <Route path={path} exact={exact}>
    <AppLayout fullWidth backgroundColor={backgroundColor}>
      {/* Tree Route lazy loads ErrorPage without a suspense, so we need to wrap it in suspense here. Also used for the lazy loaded children. */}
      <Suspense fallback={<LoadingComponent />}>
        <TreeRoute {...props} LoadingComponent={LoadingComponent} />
      </Suspense>
    </AppLayout>
  </Route>
)

type ChildrenProps = {
  children: ReactNode
}

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch everything on initial load, instead of piecemeal.
const TopLevelAppWrapper = ({ children }: ChildrenProps): JSX.Element => {
  // Pre-cache all preferences that are used in the app
  usePreloadPreferences()

  return (
    <GroupsProvider
      noLoadingGating
      LoadingComponent={AppLoading}
      SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
      SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
    >
      {children}
    </GroupsProvider>
  )
}

const DataAdminProviderWrapper = ({ children }: ChildrenProps): JSX.Element => {
  const [role, setRole] = useState<string | null>(null)

  useEffect(() => {
    let isCurrent = true
    getActiveRole()
      .then((adminType) => isCurrent && setRole(adminType))
      .catch(console.error)
    return () => {
      isCurrent = false
    }
  }, [])

  return <DataAdminProvider dataAdminRole={role}>{children}</DataAdminProvider>
}

const ResearcherToResearchTreeRedirect = (): null => {
  // Instead of using <Redirect from="/researcher*" to="/research-tree*" /> that does not keep query params, we do this instead.
  const history = useHistory()

  useEffect(() => {
    history.replace(
      `${history.location.pathname.replace(OLD_RESEARCHER_PAGE_PATH, RESEARCH_TREE_LIST_PATH)}${history.location.search}`,
      history.location.state
    )
  }, [history])
  return null
}

// ---------------- Tree Import Routes (under /groups) ----------------
// These routes were migrated from the legacy tree import app. Previously they lived at /tree/import/*.
// Legacy server redirects now map /tree/import/* -> /groups/* (see server.js).
// Admin-only routes are guarded by permission checks below.

const AppRoutes = (): JSX.Element => {
  const { signedIn } = useUser()
  const manageTreesOn = useFeatureFlag('treeWeb_manageTrees').isOn

  return (
    <Switch>
      {/* Redirect to /trees when treeWeb_manageTrees is on */}
      {manageTreesOn && (
        <Route
          exact
          path={RESEARCH_TREE_LIST_PATH}
          render={({ location }) => <Redirect to={`${TREES_PATH}${location.search}`} />}
        />
      )}

      {/*
       * The /files route is accessible only by visiting Search -> Genealogies. Normal navigation directs
       * users to /groups/files.
       * TODO: Remove this route and ChooseOldOrNewImportPage when moved from phase 2 for user trees.
       */}
      <EnhancedTreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        requirePersonalData
        exact
        path={GENEALOGIES_FILES_PATH}
        component={ChooseOldOrNewImportPage}
      />
      <EnhancedTreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        requirePersonalData
        backgroundColor={colors.gray03}
        exact
        path="/compare/:treeId"
        component={ComparePage}
      />
      <EnhancedTreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        requirePersonalData
        exact
        backgroundColor={colors.gray03}
        path="/matches/user"
        component={UserMatchesPage}
      />
      <EnhancedTreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        requirePersonalData
        exact
        path="/matches/:treeId"
        component={MatchesPage}
      />

      {/* Import admin routes */}
      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} exact path="/admin">
        <AdminRoute>
          <RealTimeStats />
        </AdminRoute>
      </EnhancedTreeRoute>
      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} exact path="/admin/stats">
        <AdminRoute>
          <RealTimeStats />
        </AdminRoute>
      </EnhancedTreeRoute>
      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} exact path="/admin/stats/:treeId">
        <AdminRoute>
          <TreeStats />
        </AdminRoute>
      </EnhancedTreeRoute>
      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} exact path="/admin/recent/:page/:size">
        <AdminRoute>
          <RecentTrees />
        </AdminRoute>
      </EnhancedTreeRoute>
      <EnhancedTreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        exact
        path="/admin/persons/:treeId/:personStatus/:matchLevel/:page/:size"
      >
        <AdminRoute>
          <TreePersons />
        </AdminRoute>
      </EnhancedTreeRoute>

      {/* Main Group Management routes */}
      {manageTreesOn && (
        <EnhancedTreeRoute
          requirePersonalData
          exact
          path={TREES_PATH}
          backgroundColor={colors.gray03}
          LoadingComponent={ManageTreesPageSkeleton}
        >
          <ManageTreesPage />
        </EnhancedTreeRoute>
      )}
      {/*
       * @deprecated The /research-tree base route below will be unreachable when treeWeb_manageTrees is persisted.
       * Note: /research-tree/:groupId/* routes (group details, changes) will still work.
       * TODO: Remove this route once flag is fully rolled out.
       */}
      <EnhancedTreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        requirePersonalData
        exact
        path={RESEARCH_TREE_LIST_PATH}
      >
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator isResearcherPage loader={<CenteredLoadingSpinner />}>
            <ChooseOldOrNewImportPage />
            <UsabillaAction type="cet" />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </EnhancedTreeRoute>

      <EnhancedTreeRoute
        LoadingComponent={GroupListSkeleton}
        requirePersonalData
        exact
        backgroundColor={colors.gray03}
        path={FAMILY_GROUP_LIST_PATH}
      >
        <GroupsLoadingGate loader={<GroupListSkeleton />}>
          <UnavailableDecorator loader={<GroupListSkeleton />}>
            <FamilyGroupsListPage />
            <UsabillaAction />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </EnhancedTreeRoute>

      <EnhancedTreeRoute
        backgroundColor={colors.gray03}
        LoadingComponent={GroupDetailsPageSkeletons}
        requirePersonalData
        exact
        path={FAMILY_GROUP_PATH}
      >
        <GroupsLoadingGate loader={<GroupDetailsPageSkeletons />}>
          <UnavailableDecorator loader={<GroupDetailsPageSkeletons />}>
            <GroupDetailsPage />
            <UsabillaAction />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </EnhancedTreeRoute>

      <Redirect exact from={TREE_SETTINGS_NO_TAB_PATH} to="/:subPage/:groupId/details" />

      <EnhancedTreeRoute
        LoadingComponent={SettingsPageSkeleton}
        requirePersonalData
        exact
        path={TREE_SETTINGS_PATH}
        backgroundColor={colors.gray03}
      >
        <UnavailableDecorator isResearcherPage loader={<SettingsPageSkeleton />}>
          <TreeSettingsPage />
          <UsabillaAction type="cet" />
        </UnavailableDecorator>
      </EnhancedTreeRoute>

      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} path={OLD_RESEARCHER_PAGE_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <ResearcherToResearchTreeRedirect />
        </GroupsLoadingGate>
      </EnhancedTreeRoute>

      <EnhancedTreeRoute LoadingComponent={TreeChangesPageSkeleton} requirePersonalData exact path={TREE_CHANGES_PATH}>
        <GroupsLoadingGate loader={<TreeChangesPageSkeleton />}>
          <UnavailableDecorator isResearcherPage loader={<TreeChangesPageSkeleton />}>
            <TreeChangesPage />
            <UsabillaAction type="cet" />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </EnhancedTreeRoute>
      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={SWITCH_TREE_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <TreeIdToGroupId />
        </GroupsLoadingGate>
      </EnhancedTreeRoute>
      <EnhancedTreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path={CONNECT_CREATE_PATH}>
        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
          <UnavailableDecorator loader={<CenteredLoadingSpinner />}>
            <ConnectCreatePage />
          </UnavailableDecorator>
        </GroupsLoadingGate>
      </EnhancedTreeRoute>

      <Route exact path={CONNECT_CREATE_DONE_PATH} />

      {/* Default import landing (signed-in users go to /family, others see landing page) */}
      <Route exact path="/" requirePersonalData>
        {signedIn ? (
          <Redirect to={FAMILY_GROUP_LIST_PATH} />
        ) : (
          <Suspense fallback={<CenteredLoadingSpinner />}>
            <LandingPage />
          </Suspense>
        )}
      </Route>
      {/* Group Management 404 inside /groups namespace */}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  )
}

export default function App(): JSX.Element {
  return (
    <TopLevelAppWrapper>
      <SubnavWrapper />
      <DataAdminProviderWrapper>
        <ErrorBoundary>
          <main id="skipToMainContent" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <AppRoutes />
          </main>
        </ErrorBoundary>
      </DataAdminProviderWrapper>
    </TopLevelAppWrapper>
  )
}
